// src/tabs/TabContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";

// 개발 중 콘솔 디버깅이 필요하면 true
const DEBUG = false;

const TabsContext = createContext(null);

// 프로젝트의 메인 경로
const MAIN_PATH = "/dashboard";
const MAIN_TAB = Object.freeze({ path: MAIN_PATH, title: "메인", icon: "🏠" });

const STORAGE_KEY = "erp_route_tabs_v3";

// ✅ 열린 탭 상한 — keep-alive 도입으로 탭 수 = 백그라운드에 마운트된 페이지 수가
// 됐으므로, 무제한으로 열리지 않게 상한을 둔다. 초과 시 메인/지금 활성화하는 탭을
// 제외하고 가장 오래 안 본(=lastActivatedAt이 가장 옛날인) 탭을 자동으로 닫는다.
const MAX_TABS = 12;

// 초기 상태: 메인 탭만 고정
const initialState = {
  activePath: "",
  tabs: [MAIN_TAB],
};

function pinMainFirst(tabs) {
  const others = tabs.filter((t) => t.path !== MAIN_PATH);
  return [MAIN_TAB, ...others];
}

function reducer(state, action) {
  switch (action.type) {
    case "LOAD": {
      const next = action.payload;
      if (!next || !Array.isArray(next.tabs)) return state;

      const restored = next.tabs.some((t) => t.path === MAIN_PATH)
        ? next.tabs
        : [MAIN_TAB, ...next.tabs];

      return {
        activePath: next.activePath ?? "",
        tabs: pinMainFirst(restored),
      };
    }

    case "ADD_or_ACTIVATE": {
      const { path, title, icon } = action.payload;
      const now = Date.now();
      const exists = state.tabs.some((t) => t.path === path);
      let tabs = exists
        ? state.tabs.map((t) => (t.path === path ? { ...t, title, icon, lastActivatedAt: now } : t))
        : [...state.tabs, { path, title, icon, lastActivatedAt: now }];
      tabs = pinMainFirst(tabs);

      // ✅ 상한 초과 시 메인/방금 활성화한 탭을 제외하고 가장 오래 안 본 탭부터 닫는다.
      if (tabs.length > MAX_TABS) {
        const closable = tabs.filter((t) => t.path !== MAIN_PATH && t.path !== path);
        const oldest = [...closable].sort((a, b) => (a.lastActivatedAt || 0) - (b.lastActivatedAt || 0))[0];
        if (oldest) tabs = tabs.filter((t) => t.path !== oldest.path);
      }

      return { ...state, tabs, activePath: path };
    }

    case "ACTIVATE": {
      return { ...state, activePath: action.payload };
    }

    case "CLOSE": {
      const closing = action.payload;
      if (closing === MAIN_PATH) return state; // 메인은 닫기 금지

      const tabs = state.tabs.filter((t) => t.path !== closing);
      const activePath =
        closing === state.activePath
          ? tabs[tabs.length - 1]?.path ?? MAIN_PATH
          : state.activePath;

      return { ...state, tabs: pinMainFirst(tabs), activePath };
    }

    case "CLOSE_OTHERS": {
      const keepPath = action.payload;

      if (keepPath === MAIN_PATH) {
        return { tabs: [MAIN_TAB], activePath: MAIN_PATH };
      }
      const keep = state.tabs.find((t) => t.path === keepPath);
      if (!keep) return { tabs: [MAIN_TAB], activePath: MAIN_PATH };
      return { tabs: [MAIN_TAB, keep], activePath: keep.path };
    }

    case "CLOSE_ALL": {
      return { tabs: [MAIN_TAB], activePath: MAIN_PATH };
    }

    // ✅ 드래그 앤 드롭 순서 변경 — 메인 탭은 항상 맨 앞 고정이라 이동 대상에서 제외.
    // toIndex는 "제거 전" 배열 기준 삽입 위치(0=맨 앞, tabs.length=맨 끝)이며,
    // 제거로 인해 뒤 원소들이 한 칸씩 당겨지는 만큼 보정해서 실제 삽입 위치를 구한다.
    case "REORDER": {
      const { from, toIndex } = action.payload;
      if (from === MAIN_PATH) return state;

      const tabs = [...state.tabs];
      const fromIdx = tabs.findIndex((t) => t.path === from);
      if (fromIdx === -1) return state;

      const [moved] = tabs.splice(fromIdx, 1);

      let insertAt = fromIdx < toIndex ? toIndex - 1 : toIndex;
      insertAt = Math.max(1, Math.min(insertAt, tabs.length)); // 메인(0번) 앞엔 못 꽂음

      tabs.splice(insertAt, 0, moved);
      return { ...state, tabs: pinMainFirst(tabs) };
    }

    default:
      return state;
  }
}

export function TabsProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // 최초 로드 시 저장된 탭 복원
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        dispatch({ type: "LOAD", payload: parsed });
        if (DEBUG) console.debug("[Tabs] LOAD →", parsed);
      }
    } catch (e) {
      if (DEBUG) console.debug("[Tabs] LOAD parse error:", e);
    }
  }, []);

  // 변경사항 저장
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      if (DEBUG) console.debug("[Tabs] SAVE →", state);
    } catch (e) {
      if (DEBUG) console.debug("[Tabs] SAVE error:", e);
    }
  }, [state]);

  const value = useMemo(() => ({ state, dispatch }), [state]);

  if (DEBUG) {
    // Provider가 렌더될 때 현재 타입을 점검
    // eslint-disable-next-line no-console
    console.debug("[TabsProvider] type:", typeof TabsProvider);
  }

  return (
    <TabsContext.Provider value={value}>{children}</TabsContext.Provider>
  );
}

export function useTabs() {
  const ctx = useContext(TabsContext);
  if (!ctx) {
    throw new Error("useTabs must be used within <TabsProvider>");
  }
  return ctx;
}
